import { RefObject, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';

export interface IDataTableFindMatch {
    rowId: string;
    rowIndex: number;
    colId: string;
}

interface IFindRow {
    id: string;
    /** Текст ячейки по id колонки; undefined — строка в поиске не участвует (строка группы). */
    text: ((colId: string) => string) | undefined;
}

interface IOptions {
    /** Запрос при монтировании */
    defaultQuery?: string;
    rows: readonly IFindRow[];
    /** Видимые колонки данных в порядке показа. */
    colIds: readonly string[];
    rowsRef: RefObject<HTMLElement | null>;
    /** Довести совпадение до видимой области (виртуализатор + горизонтальная прокрутка). */
    reveal: (match: IDataTableFindMatch) => void;
}

const HIGHLIGHT = 'dtb-find';
const HIGHLIGHT_ACTIVE = 'dtb-find-active';

const highlightsOf = () =>
    typeof CSS !== 'undefined' && 'highlights' in CSS && typeof Highlight !== 'undefined' ? CSS.highlights : null;

/** Вхождения запроса в текстовых узлах ячейки — диапазонами для CSS Custom Highlight API. */
const rangesIn = (cell: Element, needle: string): Range[] => {
    const ranges: Range[] = [];
    const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const text = node.nodeValue?.toLowerCase() ?? '';
        for (let at = text.indexOf(needle); at >= 0; at = text.indexOf(needle, at + needle.length)) {
            const range = new Range();
            range.setStart(node, at);
            range.setEnd(node, at + needle.length);
            ranges.push(range);
        }
    }
    return ranges;
};

/**
 * «Найти в таблице»: не фильтрует строки, а ведёт по совпадениям. Совпадения считаются по данным
 * (виртуализированных строк в DOM нет), подсветка рисуется поверх DOM через CSS Custom Highlight
 * API — рендер ячеек не трогается, поэтому работает с любыми кастомными ячейками. Виртуальный
 * список меняет узлы при прокрутке: подсветку пересобирает MutationObserver.
 */
export const useDataTableFind = ({ defaultQuery = '', rows, colIds, rowsRef, reveal }: IOptions) => {
    const [query, setQuery] = useState(defaultQuery);
    const [index, setIndex] = useState(0);
    const needle = useDeferredValue(query.trim().toLowerCase());

    const matches = useMemo(() => {
        if (!needle) return [];
        const found: IDataTableFindMatch[] = [];
        rows.forEach((row, rowIndex) => {
            if (!row.text) return;
            for (const colId of colIds) {
                if (row.text(colId).toLowerCase().includes(needle)) found.push({ rowId: row.id, rowIndex, colId });
            }
        });
        return found;
    }, [needle, rows, colIds]);

    const current = matches.length ? matches[Math.min(index, matches.length - 1)] : undefined;
    const currentKey = current ? `${current.rowId}|${current.colId}` : '';

    useEffect(() => setIndex(0), [needle]);

    // подсветка: все вхождения в смонтированных ячейках + отдельным слоем текущее совпадение
    useEffect(() => {
        const registry = highlightsOf();
        const list = rowsRef.current;
        if (!registry || !list || !needle) return;
        let frame = 0;
        const paint = () => {
            frame = 0;
            const all: Range[] = [];
            const active: Range[] = [];
            list.querySelectorAll<HTMLElement>('[data-row-key] [data-col]').forEach((cell) => {
                const ranges = rangesIn(cell, needle);
                if (!ranges.length) return;
                const rowId = cell.closest<HTMLElement>('[data-row-key]')?.dataset.rowKey;
                (`${rowId}|${cell.dataset.col}` === currentKey ? active : all).push(...ranges);
            });
            registry.set(HIGHLIGHT, new Highlight(...all));
            registry.set(HIGHLIGHT_ACTIVE, new Highlight(...active));
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(paint);
        };
        paint();
        const observer = new MutationObserver(schedule);
        observer.observe(list, { childList: true, subtree: true, characterData: true });
        return () => {
            observer.disconnect();
            cancelAnimationFrame(frame);
            registry.delete(HIGHLIGHT);
            registry.delete(HIGHLIGHT_ACTIVE);
        };
    }, [needle, currentKey, rowsRef]);

    const step = useCallback(
        (delta: number) => {
            if (!matches.length) return;
            const next = (Math.min(index, matches.length - 1) + delta + matches.length) % matches.length;
            setIndex(next);
            reveal(matches[next]);
        },
        [matches, index, reveal],
    );

    // новый запрос сразу показывает первое совпадение
    useEffect(() => {
        if (matches.length) reveal(matches[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needle]);

    const clear = useCallback(() => setQuery(''), []);

    return {
        query,
        setQuery,
        clear,
        step,
        count: matches.length,
        position: current ? Math.min(index, matches.length - 1) + 1 : 0,
        pending: query.trim().toLowerCase() !== needle,
    };
};
